import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteAttachment,
  listAttachments,
  uploadAttachment,
  type ApplicationAttachment,
} from "@/api/attachments";

export const ATTACHMENTS_KEY = ["attachments"] as const;

export function useAttachments(
  applicationId: string | null | undefined,
  enabled = true,
) {
  return useQuery<ApplicationAttachment[]>({
    queryKey: [...ATTACHMENTS_KEY, applicationId],
    queryFn: () => listAttachments(applicationId as string),
    enabled: !!applicationId && enabled,
    retry: false,
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();
  return useMutation<
    ApplicationAttachment,
    unknown,
    { applicationId: string; file: File }
  >({
    mutationFn: ({ applicationId, file }) =>
      uploadAttachment(applicationId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [...ATTACHMENTS_KEY, data.applicationId],
      });
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    { applicationId: string; fileId: string }
  >({
    mutationFn: ({ applicationId, fileId }) =>
      deleteAttachment(applicationId, fileId),
    onSuccess: (_void, vars) => {
      queryClient.invalidateQueries({
        queryKey: [...ATTACHMENTS_KEY, vars.applicationId],
      });
    },
  });
}
